import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import { FormFieldDto } from "api"
import {
  Control,
  FieldError,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from "react-hook-form"
import CheckboxField from "../Fields/CheckboxField"
import FileUpload from "../Fields/FileUpload"
import FormTitleText from "../Fields/FormTitleText"
import RadioField from "../Fields/RadioField"
import SelectField from "../Fields/SelectField"
import TextField from "../Fields/TextField"
import { FormFieldSettingsProps } from "./interfaces"
import { isJustText } from "./isJustText"
import { evaluateCondition } from "./validations/evaluateCondition" // Updated import

export const renderField = (
  field: FormFieldDto,
  control: Control<FieldValues>,
  errors: FieldErrors<{
    [key: string]: any
  }>,
  isSubmitted: boolean,
  setValue: UseFormSetValue<FieldValues>,
  watchedValues: FieldValues,
) => {
  const settings = field.settings as unknown as FormFieldSettingsProps

  // Helper to get the field name (use same logic as FormBuilder)
  const getFieldName = (field: any) => {
    if (field.alias === "idNumber") {
      const modifiedName = `${field.id}_idNumber`
      return modifiedName
    }
    return field.id || field.alias || field.id
  }

  const fieldName = getFieldName(field)
  const error = errors[fieldName] || null
  const shouldShow = evaluateCondition(field.condition, watchedValues) // Updated method usage
  if (field.condition?.actionType === "Show" && !shouldShow) {
    return null
  }
  if (field.condition?.actionType === "Hide" && shouldShow) {
    return null
  }
  if (field?.type?.name === "Checkbox with Rich Text") {
    return (
      <CheckboxField
        field={field}
        control={control}
        error={error as FieldError}
        isSubmitted={isSubmitted}
        fieldName={fieldName}
      />
    )
  }
  if (field.type.renderInputType === "Multiple") {
    if (field.type.supportsPreValues) {
      return (
        <RadioField
          field={field}
          control={control}
          error={error}
          isSubmitted={isSubmitted}
          fieldName={fieldName}
        />
      )
    }
    return (
      <SelectField
        field={field}
        control={control}
        error={error}
        isSubmitted={isSubmitted}
        watchedValues={watchedValues}
        fieldName={fieldName}
      />
    )
  }
  if (field.type.renderInputType === "Single") {
    if (field.type.supportsUploadTypes) {
      // Store the idNumber value in watchedValues with a special key for easy access
      let clientId = ""
      // Look for the field with alias "idNumber" in the current watchedValues
      Object.entries(watchedValues).forEach(([key, value]) => {
        // Check if this key contains idNumber information
        if (key.includes("_idNumber") || key === "idNumber") {
          clientId = String(value || "")
        }
      })
      if (field.alias === "document2") return null // remove when want to add the lead form uploads
      return (
        <FileUpload
          setValue={setValue}
          formError={error as FieldError}
          isSubmitted={isSubmitted}
          onChange={(value) =>
            setValue(fieldName, value, { shouldValidate: true })
          }
          caption={field.title || ""}
          helpText={field.helpText ?? ""}
          field={field}
          clientId={clientId}
        />
      )
    }
    if (field.type.name === "Dropdown") {
      return (
        <SelectField
          field={field}
          control={control}
          error={error}
          isSubmitted={isSubmitted}
          watchedValues={watchedValues}
          fieldName={fieldName}
        />
      )
    }
    if (isJustText(field)) {
      if (settings.html) {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(settings.html),
            }}
          />
        )
      } else {
        return <FormTitleText field={field} />
      }
    }
    if (field.type.name === "Long answer") {
      return (
        <TextField
          field={field}
          control={control}
          error={error}
          isSubmitted={isSubmitted}
          fieldName={fieldName}
        />
      )
    }
    if (
      settings.fieldType === undefined ||
      field?.type?.name === "Checkbox with Rich Text"
    ) {
      return (
        <CheckboxField
          field={field}
          control={control}
          error={error as FieldError}
          isSubmitted={isSubmitted}
          fieldName={fieldName}
        />
      )
    }
    return (
      <TextField
        field={field}
        control={control}
        error={error}
        isSubmitted={isSubmitted}
        fieldName={fieldName}
      />
    )
  }
  return null
}
